import {inject, injectable} from 'inversify'
import {action, computed, makeObservable, observable} from 'mobx'
import {saveAs} from 'file-saver'
import {
    PaymentLink,
    PaymentLinkOptionMode,
    getTerminalSettings,
    getPaymentLinkPdf,
    TerminalSettingsResponse,
    PaymentLinkPdfDownloadRequest
} from '@/api'
import {openErrorNotification} from '@/utils'
import {buildPdfRequest} from '@/stores/pay-by-link/services/buildPdfRequest'
import {PayByLinkLinkDetailsModalContainerStoreSymbol} from '@/pages/PayByLink'
import {TModalContainerStore} from '@/components/containers/ModalContainer/TModalContainerStore'
import {TPaymentLinkDetailsModalStore} from '@/pages/PayByLink/components'

@injectable()
export class PaymentLinkDetailsModalStore implements TPaymentLinkDetailsModalStore {
    private readonly _linkDetailsModalStore: TModalContainerStore
    terminalSettings: TerminalSettingsResponse = null

    constructor(
        @inject(PayByLinkLinkDetailsModalContainerStoreSymbol)
        linkDetailsModalStore: TModalContainerStore
    ) {
        this._linkDetailsModalStore = linkDetailsModalStore

        makeObservable(this, {
            isLoading: observable,
            _widgetType: observable,
            paymentLink: observable,
            isNewLinkDetails: observable,
            terminalSettings: observable,
            widgetType: computed,
            setPaymentLink: action.bound,
            closeModal: action.bound,
            reset: action.bound,
            getTerminalSettings: action.bound
        })
    }

    public isLoading = false
    public isNewLinkDetails = false
    public paymentLink: PaymentLink = {} as PaymentLink

    _widgetType: 'linkDetails' | 'shareViaEmail' | 'shareViaSMS' = 'linkDetails'

    get widgetType(): 'linkDetails' | 'shareViaEmail' | 'shareViaSMS' {
        return this._widgetType
    }

    get isMultiUse(): boolean {
        return (
            this.paymentLink?.linkOptions &&
            this.paymentLink?.linkOptions.mode !== PaymentLinkOptionMode.ONE_OFF_FIXED
        )
    }

    set widgetType(value: 'linkDetails' | 'shareViaEmail' | 'shareViaSMS') {
        this._widgetType = value
    }

    public setPaymentLink(paymentLink: PaymentLink) {
        this.paymentLink = paymentLink

        this._linkDetailsModalStore.setOpen(true)
    }

    public async getTerminalSettings(terminalId: string) {
        try {
            const {result, error} = await getTerminalSettings(terminalId)

            if (error) {
                openErrorNotification(error.message)
                return
            }

            if (result) {
                this.terminalSettings = result
            }
        } catch (error) {
            openErrorNotification(error?.message || error)
        }
    }

    public getPaymentLinkPdf = async (data: PaymentLinkPdfDownloadRequest) => {
        try {
            const request = buildPdfRequest(data)
            const {result, error} = await getPaymentLinkPdf(request)

            if (error) {
                openErrorNotification(error.message)
                return
            }

            if (result) {
                saveAs.saveAs(await result, `payment-link-${this.paymentLink?.invoiceId}`)
            }
        } catch (error) {
            openErrorNotification(error?.message || error)
        }
    }

    public reset() {
        this.closeModal()
        this.setPaymentLink(null)
    }

    public closeModal() {
        this._linkDetailsModalStore.setOpen(false)
    }
}
